import React, { useState, useCallback } from "react";
import { useEffect, useMemo } from "react";
import { marketAnalysisAPI } from "../service/AuthService";
import { Box, Spinner } from "@chakra-ui/react";
import moment from "moment/moment";
import "./MyProfitLoss.css";

function getDateRange(filterKey) {
  const todayStart = moment().startOf("day");
  const todayEnd = moment().endOf("day");

  switch (filterKey) {
    case "today":
      return {
        from: todayStart.format("YYYY-MM-DDTHH:mm:ss"),
        to: todayEnd.format("YYYY-MM-DDTHH:mm:ss"),
      };
    case "week":
      return {
        from: moment().startOf("isoWeek").format("YYYY-MM-DDTHH:mm:ss"),
        to: todayEnd.format("YYYY-MM-DDTHH:mm:ss"),
      };
    case "month":
      return {
        from: moment().startOf("month").format("YYYY-MM-DDTHH:mm:ss"),
        to: todayEnd.format("YYYY-MM-DDTHH:mm:ss"),
      };
    default:
      return {};
  }
}

export default function MyProfitLoss() {
  const [marketAnalysis, setMarketAnalysis] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetching, setFetching] = useState(false);
  const [activeFilter, setActiveFilter] = useState("all");
  const [sportFilter, setSportFilter] = useState("all");
  const [customFrom, setCustomFrom] = useState(
    moment().subtract(7, "days").format("YYYY-MM-DD")
  );
  const [customTo, setCustomTo] = useState(moment().format("YYYY-MM-DD"));

  const fetchData = useCallback(async (params = {}) => {
    setFetching(true);
    try {
      const { response } = await marketAnalysisAPI(params);
      setMarketAnalysis(response || []);
    } finally {
      setFetching(false);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const buildParams = useCallback(
    (dateRange = {}, sport = sportFilter) => ({
      ...dateRange,
      ...(sport && sport !== "all" && { sport: sport.toUpperCase() }),
    }),
    [sportFilter]
  );

  const getActiveDateParams = useCallback(() => {
    if (activeFilter === "custom") {
      if (!customFrom || !customTo) return {};
      return {
        from: moment(customFrom).startOf("day").format("YYYY-MM-DDTHH:mm:ss"),
        to: moment(customTo).endOf("day").format("YYYY-MM-DDTHH:mm:ss"),
      };
    }
    return getDateRange(activeFilter);
  }, [activeFilter, customFrom, customTo]);

  const handleFilterChange = useCallback(
    (e) => {
      const filterKey = e.target.value;
      setActiveFilter(filterKey);
      if (filterKey === "custom") return;
      const dateRange = getDateRange(filterKey);
      fetchData(buildParams(dateRange));
    },
    [fetchData, buildParams]
  );

  const handleSportChange = useCallback(
    (e) => {
      const sport = e.target.value;
      setSportFilter(sport);
      const dateRange = getActiveDateParams();
      fetchData(buildParams(dateRange, sport));
    },
    [fetchData, buildParams, getActiveDateParams]
  );

  const handleCustomApply = useCallback(() => {
    if (!customFrom || !customTo) return;
    fetchData(
      buildParams({
        from: moment(customFrom).startOf("day").format("YYYY-MM-DDTHH:mm:ss"),
        to: moment(customTo).endOf("day").format("YYYY-MM-DDTHH:mm:ss"),
      })
    );
  }, [customFrom, customTo, fetchData, buildParams]);

  const stats = useMemo(() => {
    if (!marketAnalysis || marketAnalysis.length === 0) {
      return {
        totalMarkets: 0,
        totalBets: 0,
        totalProfit: 0,
        totalLoss: 0,
        netPnl: 0,
      };
    }

    let totalBets = 0;
    let totalProfit = 0;
    let totalLoss = 0;

    marketAnalysis.forEach((game) => {
      totalBets += game.totalBets || 0;
      (game.runnerPlExposureDTOS || []).forEach((runner) => {
        const exp = runner.exposure || 0;
        if (exp > 0) totalProfit += exp;
        else totalLoss += exp;
      });
    });

    return {
      totalMarkets: marketAnalysis.length,
      totalBets,
      totalProfit,
      totalLoss,
      netPnl: totalProfit + totalLoss,
    };
  }, [marketAnalysis]);

  const sportIconMap = {
    Cricket: "fas fa-cricket",
    Soccer: "fas fa-futbol",
    Tennis: "fas fa-table-tennis",
  };

  const sportDisplayName = (sport) => (sport === "Soccer" ? "Football" : sport);

  const groupedBySport = useMemo(() => {
    if (!marketAnalysis || marketAnalysis.length === 0) return {};
    const groups = {};
    marketAnalysis.forEach((game) => {
      const raw = game.sport || "Other";
      const sport = raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase();
      if (!groups[sport]) groups[sport] = [];
      groups[sport].push(game);
    });
    return groups;
  }, [marketAnalysis]);

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        width="100%"
        height="100vh"
      >
        <Spinner size="xl" thickness="4px" speed="0.65s" color="yellow.500" />
      </Box>
    );
  }

  const getGameUrl = (game) =>
    `/admin/game/${game?.sport?.toLocaleLowerCase()}/${game.eventId}`;

  const getExposureClass = (exposure) => (exposure < 0 ? "loss" : "profit");

  const formatNumber = (num) => {
    if (num === undefined || num === null) return "0";
    return Number(num).toLocaleString("en-IN", { maximumFractionDigits: 2 });
  };

  return (
    <div _ngcontent-oec-c45 className="col-md-12 main-container">
      <div _ngcontent-oec-c45 className="col-md-12 main-container">
        {/* Market Stats */}
        <section className="stats-dashboard stats-dashboard--market" aria-label="Market Statistics">
          <h3 className="stats-section-title">
            <i className="fas fa-chart-line" /> Market Overview
          </h3>
          <div className={`stats-grid${fetching ? " ma-refetch-overlay" : ""}`}>
            <article className="stat-card stat-card--markets">
              <div className="stat-card__icon">
                <i className="fas fa-chart-bar" />
              </div>
              <div className="stat-card__content">
                <span className="stat-card__value">{stats.totalMarkets}</span>
                <span className="stat-card__label">Active Markets</span>
              </div>
            </article>

            <article className="stat-card stat-card--bets">
              <div className="stat-card__icon">
                <i className="fas fa-receipt" />
              </div>
              <div className="stat-card__content">
                <span className="stat-card__value">{stats.totalBets}</span>
                <span className="stat-card__label">Total Bets</span>
              </div>
            </article>

            <article className="stat-card stat-card--profit">
              <div className="stat-card__icon">
                <i className="fas fa-arrow-up" />
              </div>
              <div className="stat-card__content">
                <span className="stat-card__value profit">
                  {formatNumber(stats.totalProfit)}
                </span>
                <span className="stat-card__label">Total Profit</span>
              </div>
            </article>

            <article className="stat-card stat-card--loss">
              <div className="stat-card__icon">
                <i className="fas fa-arrow-down" />
              </div>
              <div className="stat-card__content">
                <span className="stat-card__value loss">
                  {formatNumber(stats.totalLoss)}
                </span>
                <span className="stat-card__label">Total Loss</span>
              </div>
            </article>

            <article className="stat-card stat-card--pnl">
              <div className="stat-card__icon">
                <i className="fas fa-balance-scale" />
              </div>
              <div className="stat-card__content">
                <span
                  className={`stat-card__value ${
                    stats.netPnl >= 0 ? "profit" : "loss"
                  }`}
                >
                  {formatNumber(stats.netPnl)}
                </span>
                <span className="stat-card__label">Net P&L</span>
              </div>
            </article>
          </div>
        </section>

        {/* Filters */}
        <div className="date-filter-bar">
          <select
            className="date-filter-select"
            value={sportFilter}
            onChange={handleSportChange}
            aria-label="Filter by sport"
          >
            <option value="all">All Sports</option>
            <option value="cricket">Cricket</option>
            <option value="soccer">Football</option>
            <option value="tennis">Tennis</option>
          </select>

          <select
            className="date-filter-select"
            value={activeFilter}
            onChange={handleFilterChange}
            aria-label="Filter by date range"
          >
            <option value="all">All</option>
            <option value="today">Today</option>
            <option value="week">This Week</option>
            <option value="month">This Month</option>
            <option value="custom">Custom Range</option>
          </select>

          {activeFilter === "custom" && (
            <div className="date-filter-custom">
              <input
                type="date"
                value={customFrom}
                onChange={(e) => setCustomFrom(e.target.value)}
                aria-label="From date"
                className="date-filter-input"
              />
              <span className="date-filter-separator">to</span>
              <input
                type="date"
                value={customTo}
                onChange={(e) => setCustomTo(e.target.value)}
                aria-label="To date"
                className="date-filter-input"
              />
              <button
                type="button"
                className="date-filter-apply"
                onClick={handleCustomApply}
                disabled={!customFrom || !customTo || fetching}
              >
                {fetching ? "Loading..." : "Apply"}
              </button>
            </div>
          )}

          {fetching && (
            <Spinner size="sm" thickness="3px" speed="0.65s" color="yellow.500" />
          )}
        </div>

        <div _ngcontent-oec-c45 className="listing-grid">
          <div _ngcontent-oec-c45 className="detail-row">
            <h2 _ngcontent-oec-c45>Market Analysis</h2>
            <div _ngcontent-oec-c45 className="m-t-10 m-b-10">
              You can view your cricket card books from sport menu
            </div>
          </div>

          <div className={fetching ? "ma-refetch-overlay" : ""}>
            {Object.entries(groupedBySport).map(([sport, games]) => (
              <section className="ma-sport-group" key={sport}>
                <h4 className="ma-sport-header">
                  <i className={sportIconMap[sport] || "fas fa-trophy"} />
                  {sportDisplayName(sport)}
                  <span className="ma-sport-count">{games.length}</span>
                </h4>
                <div className="ma-cards-container">
                  {games.map((game) => (
                    <article className="ma-card" key={game.eventId}>
                      <div className="ma-card-header">
                        <a href={getGameUrl(game)}>
                          {game.eventName} -{" "}
                          {moment(game.eventDate).format("DD/MM/YYYY HH:mm")}
                        </a>
                      </div>
                      <div className="ma-card-body">
                        <div className="ma-card-stat">
                          <span className="ma-card-stat-label">
                            {game?.runnerPlExposureDTOS[0]?.selectionName || "Team 1"}
                          </span>
                          <span
                            className={`ma-card-stat-value ${getExposureClass(
                              game?.runnerPlExposureDTOS[0]?.exposure
                            )}`}
                          >
                            {game?.runnerPlExposureDTOS[0]?.exposure}
                          </span>
                        </div>
                        <div className="ma-card-stat">
                          <span className="ma-card-stat-label">
                            {game?.runnerPlExposureDTOS[1]?.selectionName || "Team 2"}
                          </span>
                          <span
                            className={`ma-card-stat-value ${getExposureClass(
                              game?.runnerPlExposureDTOS[1]?.exposure
                            )}`}
                          >
                            {game?.runnerPlExposureDTOS[1]?.exposure}
                          </span>
                        </div>
                        <div className="ma-card-stat">
                          <span className="ma-card-stat-label">The Draw</span>
                          <span
                            className={`ma-card-stat-value ${getExposureClass(
                              game?.runnerPlExposureDTOS[2]?.exposure
                            )}`}
                          >
                            {game?.runnerPlExposureDTOS[2]?.exposure
                              ? game?.runnerPlExposureDTOS[2]?.exposure
                              : 0}
                          </span>
                        </div>
                        <div className="ma-card-stat">
                          <span className="ma-card-stat-label">Total Bets</span>
                          <span className="ma-card-stat-value profit">
                            {game.totalBets}
                          </span>
                        </div>
                      </div>
                    </article>
                  ))}
                </div>
              </section>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
